import React, { useEffect, useMemo, useState } from "react";
import AddProduct from "./AddProduct";
import "./products.css";
import { useVendorAuth } from "../../store/vendorAuthStore";

const API_BASE = "https://xiadot.com/vendor_api";

const PLACEMENT_OPTIONS = [
  { key: "best_product", icon: "⭐", label: "Best Product", color: "#b45309", bg: "#fffbeb" },
  { key: "today_offer", icon: "🎁", label: "Today Offer", color: "#9d174d", bg: "#fdf2f8" },
  { key: "flash_sale", icon: "⚡", label: "Flash Sale", color: "#b91c1c", bg: "#fff5f5" },
];

function toImageUrl(path) {
  if (!path) return "";
  var p = String(path).replace(/\/(thumb_|thumb-|thumbnail_)/i, '/');
  if (p.indexOf("http://") === 0 || p.indexOf("https://") === 0) return p;
  return API_BASE + "/" + p.replace(/^\/+/, "");
}

function num(v) {
  var n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function pickOfferPrice(obj) {
  return num(obj.offer_price != null ? obj.offer_price : obj.offerPrice != null ? obj.offerPrice : 0);
}

function unitLabel(unit, unit_qty) {
  if (!unit) return "piece";
  var qty = num(unit_qty);
  if (qty > 0 && qty !== 1) return qty + " " + unit;
  return unit;
}

// ✅ Check vendor approval
function isVendorApproved(vendor) {
  if (!vendor) return false;
  var val = vendor.is_approved;
  if (val == null) return false;
  if (val === 1 || val === "1" || val === true) return true;
  if (val === 0 || val === "0" || val === false) return false;
  var s = String(val).toLowerCase();
  return s === "approved" || s === "active";
}

// ─────────────────────────────────────────────────────────────────────────────
//  ✅ Final price calculator for product cards
//
//  Formula (identical to PriceCalculator & backend):
//    vendorEarning = offer_price (if > 0) else mrp
//    commissionAmt = vendorEarning × (commission% / 100)
//    finalPrice    = vendorEarning + commissionAmt + delivery   ← customer pays
//    vendorEarning = vendorEarning (offer price is what vendor earns)
// ─────────────────────────────────────────────────────────────────────────────


// ✅ Compact view button shown on each product card with variants
function CardPriceSummary({ product, openVariantDetails }) {
  if (!product.product_variants || product.product_variants.length === 0) return null;

  return (
    <div style={{
      margin: "8px 0 4px",
      background: "linear-gradient(135deg, #f0f4ff 0%, #eef2ff 100%)",
      border: "1px solid #c7d2fe",
      borderRadius: 8,
      padding: "10px 11px",
    }}>
      <button
        className="vp-btn"
        style={{
          width: "100%",
          background: "#4f46e5",
          color: "#fff",
          padding: "8px 16px",
          borderRadius: 6,
          fontSize: 12,
          fontWeight: 600,
          border: "none",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 6
        }}
        onClick={function () { openVariantDetails(product); }}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
          <circle cx="11" cy="11" r="8" />
          <path d="m21 21-4.35-4.35" />
          <line x1="11" y1="8" x2="11" y2="14" />
          <line x1="8" y1="11" x2="14" y2="11" />
        </svg>
        View Pricing Details
      </button>
    </div>
  );
}

// ✅ Pending Approval Banner
function PendingApprovalBanner() {
  return (
    <div style={{
      display: "flex", alignItems: "flex-start", gap: 14,
      background: "linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)",
      border: "1px solid #7dd3fc", borderLeft: "4px solid #0284c7",
      borderRadius: 12, padding: "16px 18px", margin: "0 0 20px",
    }}>
      <div style={{
        flexShrink: 0, width: 42, height: 42, borderRadius: 10,
        background: "#0284c7", display: "flex", alignItems: "center", justifyContent: "center",
      }}>
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
        </svg>
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 5 }}>
          <span style={{ fontWeight: 700, fontSize: 14, color: "#0c4a6e" }}>Account Pending Approval</span>
          <span style={{
            display: "inline-flex", alignItems: "center", gap: 4,
            background: "#fef9c3", color: "#854d0e", border: "1px solid #fde047",
            fontSize: 11, fontWeight: 700, borderRadius: 20, padding: "2px 10px", letterSpacing: 0.2,
          }}>
            <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#ca8a04", display: "inline-block", animation: "vp-pulse 1.5s infinite" }} />
            Under Review
          </span>
        </div>
        <p style={{ margin: 0, fontSize: 13, color: "#075985", lineHeight: 1.6 }}>
          Your vendor account is currently under review by the admin. You'll be able to
          add and manage products once your account is <strong>approved</strong>.
        </p>
      </div>
    </div>
  );
}

// ── Variant Details Modal ───────────────────────────────────────────
function VariantDetailsModal({ product, commission, onClose }) {
  if (!product || !product.product_variants || product.product_variants.length === 0) return null;

  var deliveryCharge = num(product.delivery_charge);
  var commissionNum = num(commission);

  return (
    <div className="vp-overlay" onMouseDown={onClose}>
      <div className="vp-img-modal" onMouseDown={function (e) { e.stopPropagation(); }} style={{ maxWidth: "650px", maxHeight: "80vh", display: "flex", flexDirection: "column" }}>
        <div className="vp-img-head">
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span className="vp-img-title">Variants - {product.name}</span>
            <span style={{ fontSize: 11, color: "var(--text-3)", background: "var(--bg)", padding: "2px 8px", borderRadius: 20 }}>
              {product.product_variants.length} variants
            </span>
          </div>
          <button className="vp-close-btn" onClick={onClose}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <div className="vp-img-body" style={{ padding: "20px", overflowY: "auto", flex: 1 }}>
          {/* Customized Values Display */}
          {product.customized_values && product.customized_values.length > 0 && (
            <div style={{ marginBottom: 20 }}>
              <h4 style={{ fontSize: 14, fontWeight: 600, color: "#374151", marginBottom: 8 }}>Customized Values:</h4>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {product.customized_values.map(function (val, idx) {
                  return (
                    <span key={idx} style={{
                      padding: "4px 10px", background: "#f3f4f6",
                      border: "1px solid #d1d5db", borderRadius: 6,
                      fontSize: 12, color: "#374151"
                    }}>
                      {val}
                    </span>
                  );
                })}
              </div>
            </div>
          )}

          {/* Variants Table */}
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
              <thead>
                <tr style={{ background: "#f9fafb", borderBottom: "2px solid #e5e7eb" }}>
                  <th style={{ padding: "10px", textAlign: "left", fontWeight: 600, color: "#374151" }}>Variant</th>
                  <th style={{ padding: "10px", textAlign: "right", fontWeight: 600, color: "#374151" }}>MRP</th>
                  <th style={{ padding: "10px", textAlign: "right", fontWeight: 600, color: "#374151" }}>Offer</th>
                  <th style={{ padding: "10px", textAlign: "right", fontWeight: 600, color: "#374151" }}>Stock</th>
                </tr>
              </thead>
              <tbody>
                {product.product_variants.map(function (variant, idx) {
                  // Offer price is what vendor earns, not base for commission
                  var vendorEarning = variant.offer_price > 0 ? variant.offer_price : variant.mrp_price;
                  // commissionAmt is calculated but not used in display
                  // finalPrice is calculated but not used in display
                  var hasOffer = variant.offer_price > 0 && variant.offer_price < variant.mrp_price;

                  return (
                    <tr key={idx} style={{ borderBottom: "1px solid #f3f4f6" }}>
                      <td style={{ padding: "12px 10px", fontWeight: 600, color: "#1f2937" }}>
                        {variant.value}
                      </td>
                      <td style={{ padding: "12px 10px", textAlign: "right", color: hasOffer ? "#9ca3af" : "#374151", textDecoration: hasOffer ? "line-through" : "none" }}>
                        ₹{variant.mrp_price.toFixed(2)}
                      </td>
                      <td style={{ padding: "12px 10px", textAlign: "right", color: hasOffer ? "#059669" : "#9ca3af", fontWeight: hasOffer ? 600 : 400 }}>
                        {hasOffer ? `₹${variant.offer_price.toFixed(2)}` : "-"}
                      </td>
                      <td style={{ padding: "12px 10px", textAlign: "right", color: variant.stock_quantity > 0 ? "#059669" : "#dc2626", fontWeight: 600 }}>
                        {variant.stock_quantity}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Summary */}
          <div style={{ marginTop: 20, padding: "12px", background: "#f8fafc", borderRadius: 6, border: "1px solid #e2e8f0" }}>
            <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 4 }}>Price Calculation Details:</div>
            <div style={{ fontSize: 12, color: "#374151" }}>
              • Final Price = Vendor Earning + Commission ({commissionNum}%) + Delivery (₹{deliveryCharge.toFixed(2)})<br />
              • Vendor Earning = Offer Price (if available) else MRP<br />
              • Total Variants: {product.product_variants.length} | Total Stock: {product.product_variants.reduce(function (sum, v) { return sum + v.stock_quantity; }, 0)}
            </div>
          </div>

          {/* Cancel Button */}
          <div style={{ marginTop: 20, paddingTop: 16, borderTop: "1px solid #e5e7eb", textAlign: "center" }}>
            <button
              className="vp-btn"
              onClick={onClose}
              style={{
                background: "#6b7280",
                color: "#fff",
                padding: "10px 24px",
                borderRadius: 6,
                fontSize: 14,
                fontWeight: 500,
                border: "none",
                cursor: "pointer",
                minWidth: "100px"
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Image viewer modal ───────────────────────────────────────────
function ViewImagesModal({ title, images, onClose }) {
  var [active, setActive] = useState(0);
  return (
    <div className="vp-overlay" onMouseDown={onClose}>
      <div className="vp-img-modal" onMouseDown={function (e) { e.stopPropagation(); }}>
        <div className="vp-img-head">
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span className="vp-img-title">{title}</span>
            <span style={{ fontSize: 11, color: "var(--text-3)", background: "var(--bg)", padding: "2px 8px", borderRadius: 20 }}>
              {active + 1} / {images.length}
            </span>
          </div>
          <button className="vp-close-btn" onClick={onClose}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
        <div className="vp-img-body">
          <div className="vp-img-main">
            {images[active] && images[active].match(/\.(mp4|webm|ogg)$/i) ? (
              <video src={images[active].replace('/thumb_', '/')} autoPlay muted loop playsInline style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
            ) : (
              <img src={images[active] ? images[active].replace('/thumb_', '/') : ""} alt="product"
                onError={function (e) { e.currentTarget.src = "https://via.placeholder.com/700x450?text=No+Image"; }} />
            )}
          </div>
          {images.length > 1 && (
            <div className="vp-img-thumbs">
              {images.map(function (src, i) {
                return (
                  <button key={i} className={"vp-thumb" + (i === active ? " active" : "")}
                    onClick={function () { setActive(i); }}>
                    {src.match(/\.(mp4|webm|ogg)$/i) ? (
                      <video src={src} muted playsInline style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    ) : (
                      <img src={src} alt={"thumb-" + i}
                        onError={function (e) { e.currentTarget.src = "https://via.placeholder.com/120x90?text=No+Image"; }} />
                    )}
                    {i === 0 && (
                      <span style={{ position: "absolute", bottom: 2, left: 2, background: "rgba(15,23,42,0.7)", color: "#fff", fontSize: 9, fontWeight: 700, padding: "1px 5px", borderRadius: 8 }}>Main</span>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function ProductList() {
  var auth = useVendorAuth();
  var vendor = auth.vendor;
  var token = auth.token;
  var myVendorId = vendor ? (vendor.id || vendor._id) : null;

  var approved = isVendorApproved(vendor);

  var [products, setProducts] = useState([]);
  var [q, setQ] = useState("");
  var [openAdd, setOpenAdd] = useState(false);
  var [editOpen, setEditOpen] = useState(false);
  var [editItem, setEditItem] = useState(null);
  var [viewOpen, setViewOpen] = useState(false);
  var [viewImages, setViewImages] = useState([]);
  var [viewTitle, setViewTitle] = useState("");
  var [loading, setLoading] = useState(false);
  var [deletingId, setDeletingId] = useState(null);
  var [variantOpen, setVariantOpen] = useState(false);
  var [selectedProduct, setSelectedProduct] = useState(null);

  // ✅ Store commission per category: { [main_category_id]: commissionPercent }
  var [categoryCommissions, setCategoryCommissions] = useState({});

  var fetchProducts = async function () {
    if (!myVendorId) { setProducts([]); return; }
    setLoading(true);
    try {
      var res = await fetch(API_BASE + "/vendor/products/list.php?vendor_id=" + myVendorId + "&_t=" + Date.now(), {
        method: "GET",
        headers: { "Content-Type": "application/json", Authorization: "Bearer " + token },
        cache: "no-store",
      });
      var data = await res.json();
      if (data.success && Array.isArray(data.products)) {
        var mine = data.products.filter(function (p) { return num(p.vendor_id) === num(myVendorId); });

        var productsWithImages = await Promise.all(mine.map(async function (r) {
          var mainImgUrl = r.image ? toImageUrl(r.image) : null;
          var allImages = mainImgUrl ? [mainImgUrl] : [];
          var productVariants = [];
          var customizedValues = [];

          try {
            var detRes = await fetch(API_BASE + "/user/get-product-details.php?id=" + r.id + "&_t=" + Date.now(), { cache: "no-store" });
            var detData = await detRes.json();
            if (detData.success) {
              // Get images
              if (Array.isArray(detData.data.images) && detData.data.images.length > 0) {
                allImages = detData.data.images.filter(Boolean).map(function (img) { return toImageUrl(img); });
              }

              // Get variants
              if (Array.isArray(detData.data.product_variants)) {
                productVariants = detData.data.product_variants.map(function (v) {
                  return {
                    value: v.value || "",
                    mrp_price: num(v.mrp_price),
                    offer_price: num(v.offer_price),
                    stock_quantity: num(v.stock_quantity)
                  };
                });
              }

              // Get customized values
              if (Array.isArray(detData.data.customized_values)) {
                customizedValues = detData.data.customized_values;
              }
            }
          } catch (e) { }

          return {
            id: num(r.id),
            name: r.name || r.product_name || "",
            description: r.description || "",
            mrp: num(r.price != null ? r.price : r.mrp),
            offer_price: pickOfferPrice(r),
            delivery_charge: num(r.delivery_charge != null ? r.delivery_charge : 0),
            unit: r.unit || "piece",
            unit_qty: num(r.unit_qty != null ? r.unit_qty : 1),
            stock: num(r.stock || 0),
            status: String(r.is_active != null ? r.is_active : 1) === "1" ? "Active" : "Inactive",
            images: allImages,
            placement_tags: Array.isArray(r.placement_tags) ? r.placement_tags : [],
            main_category_id: String(r.main_category_id != null ? r.main_category_id : r.category_id != null ? r.category_id : ""),
            sub_category_id: String(r.sub_category_id != null ? r.sub_category_id : ""),
            category_id: String(r.category_id != null ? r.category_id : ""),
            product_variants: productVariants,
            customized_values: customizedValues,
            is_restrict: num(detData?.data?.is_restrict ?? r.is_restrict ?? 0),
          };
        }));
        setProducts(productsWithImages);
      } else {
        setProducts([]);
      }
    } catch (e) {
      console.error("Fetch products error:", e.message);
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  // ✅ Fetch main categories to get commission rates
  var fetchCategoryCommissions = async function () {
    try {
      var urls = [
        API_BASE + "/admin/main_categories/list.php",
        API_BASE + "/main_categories/list.php",
        API_BASE + "/vendor/main_categories/list.php",
      ];
      var data = null;
      for (var i = 0; i < urls.length; i++) {
        try {
          var r = await fetch(urls[i], { headers: { Authorization: "Bearer " + token } });
          if (!r.ok) continue;
          data = await r.json().catch(function () { return null; });
          if (data) break;
        } catch (e) { }
      }
      if (data) {
        var list = data.categories || data.main_categories || data.data || [];
        var map = {};
        (Array.isArray(list) ? list : []).forEach(function (c) {
          var cid = String(c.id != null ? c.id : c.category_id != null ? c.category_id : "");
          var comm =
            c.commission != null ? Number(c.commission)
              : c.commission_percent != null ? Number(c.commission_percent)
                : c.commission_percentage != null ? Number(c.commission_percentage)
                  : null;
          if (cid && comm != null) map[cid] = comm;
        });
        setCategoryCommissions(map);
      }
    } catch (e) { }
  };

  useEffect(function () {
    fetchProducts();
    fetchCategoryCommissions();
  }, [myVendorId, token]); // eslint-disable-line

  var filtered = useMemo(function () {
    var s = q.trim().toLowerCase();
    if (!s) return products;
    return products.filter(function (p) { return (p.name || "").toLowerCase().indexOf(s) !== -1; });
  }, [q, products]);

  var onDelete = async function (id) {
    if (!window.confirm("Delete this product?")) return;
    setDeletingId(id);
    try {
      var res = await fetch(API_BASE + "/vendor/products/delete.php", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: "Bearer " + token },
        body: JSON.stringify({ id: id }),
      });
      var data = await res.json().catch(function () { return {}; });
      if (!res.ok || data.success === false) { alert(data.message || "Delete failed"); return; }
      setProducts(function (prev) { return prev.filter(function (p) { return p.id !== id; }); });
    } catch (e) {
      alert("Delete error: " + (e.message || "Unknown error"));
    } finally {
      setDeletingId(null);
    }
  };

  var onSave = async function (payload) {
    if (!payload) { await fetchProducts(); setOpenAdd(false); return; }
    setOpenAdd(false); await fetchProducts();
  };

  var onUpdate = async function (payload) {
    if (!payload) { await fetchProducts(); setEditOpen(false); setEditItem(null); return; }
    setEditOpen(false); setEditItem(null); await fetchProducts();
  };

  var openImages = function (product) {
    var imgs = product.images || [];
    if (!imgs.length) { alert("No images for this product"); return; }
    setViewTitle(product.name || "Product Images");
    setViewImages(imgs);
    setViewOpen(true);
  };

  var openVariantDetails = function (product) {
    setSelectedProduct(product);
    setVariantOpen(true);
  };

  return (
    <div className="vp-page">

      <style>{`
        @keyframes vp-pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50%       { opacity: 0.4; transform: scale(1.4); }
        }
      `}</style>

      {/* Header */}
      <div className="vp-header">
        <div className="vp-header-left">
          <div className="vp-header-icon">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z" />
            </svg>
          </div>
          <div>
            <h1 className="vp-title">My Products</h1>
            <p className="vp-subtitle">
              {loading ? "Loading..." : products.length + " " + (products.length === 1 ? "product" : "products") + " in your store"}
            </p>
          </div>
        </div>
        <div className="vp-header-actions">
          {approved ? (
            <button className="vp-add-btn" onClick={function () { setOpenAdd(true); }}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
              </svg>
              Add Product
            </button>
          ) : (
            <div style={{ position: "relative", display: "inline-flex" }} title="Your account must be approved before you can add products.">
              <button className="vp-add-btn" disabled style={{ opacity: 0.45, cursor: "not-allowed", filter: "grayscale(0.4)", pointerEvents: "none" }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2" /><path d="M7 11V7a5 5 0 0110 0v4" />
                </svg>
                Add Product
              </button>
            </div>
          )}
        </div>
      </div>

      {!approved && <PendingApprovalBanner />}

      {/* Toolbar */}
      <div className="vp-toolbar">
        <div className="vp-search">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input value={q} onChange={function (e) { setQ(e.target.value); }} placeholder="Search products…" />
          {q && (
            <button className="vp-search-clear" onClick={function () { setQ(""); }}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          )}
        </div>
        <div className="vp-count">
          Showing <strong>{filtered.length}</strong> of <strong>{products.length}</strong>
        </div>
      </div>

      {/* Cards */}
      {loading && products.length === 0 ? (
        <div className="vp-state">
          <div className="vp-empty-icon">⏳</div>
          <p className="vp-empty-title">Loading products...</p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="vp-state">
          <div className="vp-empty-icon">
            <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
              <path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z" />
            </svg>
          </div>
          <p className="vp-empty-title">{q ? "No results found" : "No products yet"}</p>
          <p className="vp-empty-sub">
            {q ? "No products match \"" + q + "\"" : approved ? "Click Add Product to get started" : "You'll be able to add products once your account is approved"}
          </p>
        </div>
      ) : (
        <div className="vp-grid">
          {filtered.map(function (p) {
            var uLabel = unitLabel(p.unit, p.unit_qty);
            var imgCount = p.images ? p.images.length : 0;
            // commission is calculated but not used in display

            return (
              <div className="vp-card" key={p.id}>
                <div className="vp-card-img" onClick={function () { openImages(p); }}>
                  {p.images[0] ? (
                    p.images[0].match(/\.(mp4|webm|ogg)$/i) ? (
                      <video src={p.images[0]} className="vp-card-img-preview" autoPlay muted loop playsInline style={{ width: '100%', height: '100%', objectFit: 'cover', position: 'absolute', top: 0, left: 0 }} />
                    ) : (
                      <img src={p.images[0]} alt={p.name}
                        onError={function (e) { e.currentTarget.style.display = "none"; e.currentTarget.nextSibling.style.display = "flex"; }} />
                    )
                  ) : null}
                  <div className="vp-card-img-fallback" style={{ display: p.images[0] ? "none" : "flex" }}>
                    {(p.name || "P").charAt(0).toUpperCase()}
                  </div>
                  <div className="vp-card-img-overlay">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" />
                    </svg>
                    View {imgCount > 1 ? "(" + imgCount + " photos)" : ""}
                  </div>
                  {/* Only show stock overlay for products without variants */}
                  {(!p.product_variants || p.product_variants.length === 0) && (
                    <div className={"vp-card-stock" + (num(p.stock) === 0 ? " vp-stock-zero" : num(p.stock) < 5 ? " vp-stock-low" : "")}>
                      {num(p.stock)} in stock
                    </div>
                  )}
                  {imgCount > 1 && (
                    <div style={{
                      position: "absolute", bottom: 8, right: 8,
                      background: "rgba(15,23,42,0.7)", color: "#fff",
                      fontSize: 10, fontWeight: 700, padding: "2px 7px",
                      borderRadius: 20, backdropFilter: "blur(4px)",
                    }}>
                      📷 {imgCount}
                    </div>
                  )}
                </div>

                {p.placement_tags && p.placement_tags.length > 0 && (
                  <div className="vp-tag-pills">
                    {p.placement_tags.map(function (tag) {
                      var opt = PLACEMENT_OPTIONS.find(function (o) { return o.key === tag; });
                      if (!opt) return null;
                      return (
                        <span key={tag} className="vp-tag-pill" style={{ color: opt.color, background: opt.bg }}>
                          {opt.icon} {opt.label}
                        </span>
                      );
                    })}
                  </div>
                )}

                <div className="vp-card-body">
                  <div className="vp-card-top">
                    <h3 className="vp-card-name">{p.name}</h3>
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4 }}>
                      <span className={"vp-badge" + (p.status === "Active" ? " vp-badge-green" : " vp-badge-gray")}>
                        <span className="vp-badge-dot" />{p.status}
                      </span>
                      {p.is_restrict === 1 && (
                        <span style={{
                          display: "inline-flex", alignItems: "center", gap: 4,
                          background: "#fef3c7", color: "#92400e",
                          border: "1px solid #f59e0b",
                          fontSize: 10, fontWeight: 700, borderRadius: 20,
                          padding: "2px 8px", whiteSpace: "nowrap",
                        }}>
                          🔒 1 per user
                        </span>
                      )}
                    </div>
                  </div>

                  {uLabel ? (
                    <div style={{ marginBottom: 4 }}>
                      <span style={{ display: "inline-block", padding: "2px 8px", borderRadius: 6, background: "#f0f9ff", color: "#0369a1", fontSize: 11.5, fontWeight: 600 }}>
                        {uLabel}
                      </span>
                    </div>
                  ) : null}

                  {p.description && (
                    <p className="vp-card-desc">{p.description.slice(0, 55)}{p.description.length > 55 ? "…" : ""}</p>
                  )}

                  {/* Only show pricing for products without variants */}
                  {(!p.product_variants || p.product_variants.length === 0) && (
                    <>
                      {/* Original MRP / Offer pricing */}
                      <div className="vp-card-pricing">
                        <span
                          className="vp-card-mrp"
                          style={{
                            textDecoration: num(p.offer_price) > 0 ? "line-through" : "none",
                            color: num(p.offer_price) > 0 ? "#9ca3af" : "#111827",
                            fontWeight: 500,
                          }}
                        >
                          ₹{num(p.mrp).toFixed(2)}
                        </span>

                        {num(p.offer_price) > 0 && (
                          <span
                            className="vp-card-offer"
                            style={{
                              color: "#16a34a",
                              fontWeight: 700,
                              marginLeft: 8,
                            }}
                          >
                            ₹{num(p.offer_price).toFixed(2)}
                          </span>
                        )}
                      </div>

                      {/* ✅ View pricing details button */}
                      <CardPriceSummary
                        product={p}
                        openVariantDetails={openVariantDetails}
                      />
                    </>
                  )}

                  <div className="vp-card-footer">
                    <button className="vp-icon-btn vp-icon-edit"
                      onClick={function () { setEditItem(p); setEditOpen(true); }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                        <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
                        <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
                      </svg>
                      Edit
                    </button>
                    {p.product_variants && p.product_variants.length > 0 && (
                      <button className="vp-icon-btn" style={{ background: "#f0f9ff", color: "#0369a1", border: "1px solid #bae6fd" }}
                        onClick={function () { openVariantDetails(p); }}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                          <circle cx="11" cy="11" r="8" />
                          <path d="m21 21-4.35-4.35" />
                          <line x1="11" y1="8" x2="11" y2="14" />
                          <line x1="8" y1="11" x2="14" y2="11" />
                        </svg>
                        View More
                      </button>
                    )}
                    <button className="vp-icon-btn vp-icon-delete" disabled={deletingId === p.id}
                      onClick={function () { onDelete(p.id); }}>
                      {deletingId === p.id ? (
                        <span className="vp-btn-spinner" style={{ width: 12, height: 12 }} />
                      ) : (
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                          <polyline points="3 6 5 6 21 6" />
                          <path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6" />
                          <path d="M10 11v6" /><path d="M14 11v6" />
                          <path d="M9 6V4a1 1 0 011-1h4a1 1 0 011 1v2" />
                        </svg>
                      )}
                      {deletingId === p.id ? "Deleting…" : "Delete"}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {openAdd && approved && (
        <AddProduct mode="add" onClose={function () { setOpenAdd(false); }} onSave={onSave} />
      )}
      {editOpen && editItem && (
        <AddProduct
          key={editItem.id}
          mode="edit"
          initialData={editItem}
          onClose={function () { setEditOpen(false); setEditItem(null); }}
          onUpdate={onUpdate} />
      )}
      {viewOpen && (
        <ViewImagesModal title={viewTitle} images={viewImages} onClose={function () { setViewOpen(false); }} />
      )}
      {variantOpen && selectedProduct && (
        <VariantDetailsModal
          product={selectedProduct}
          commission={categoryCommissions[selectedProduct.main_category_id]}
          onClose={function () { setVariantOpen(false); setSelectedProduct(null); }}
        />
      )}
    </div>
  );
}